import React, { FC } from "react"
import { Form, Input, Select, Switch, Divider } from "antd"
import { attributionPlatformOptions, gamePlatformOptions } from "../constants"
import { GamePublisherOption } from "../store/types"
import { urlValidator } from "../helper"

interface Props {

  /** Index of this platform in form.values.platforms. */
  index: number
  publishers: GamePublisherOption[]

  /** When true, the platform itself can't be changed (edit-existing case). */
  platformLocked?: boolean

  /**
   * Options shown in the Platform dropdown. Defaults to both ANDROID/IOS.
   * The edit modal narrows this so admins can't pick a platform that's
   * already on the game (which would collide with the (gameId, platform)
   * unique key on the server).
   */
  availablePlatforms?: Array<{ value: string; label: string }>
}

/**
 * Renders one platform entry's fields using direct ["platforms", index, field]
 * paths. Decoupled from Form.List on purpose — we used to wrap the Tabs strip
 * in a root Form.List, which made Form.List re-render (and visibly remount the
 * Global tab's inputs) on every keystroke. Direct paths sidestep that.
 *
 * Used by both the "Add Game" and "Edit Game" modals so the per-platform
 * shape stays consistent between create and update flows.
 */
export const PlatformFormFields: FC<Props> = ({
  index,
  publishers,
  platformLocked,
  availablePlatforms,
}) => {
  const path = (subfield: string): (string | number)[] => ["platforms", index, subfield]
  const platformOptions = availablePlatforms ?? gamePlatformOptions

  return (
    <>
      <Form.Item
        label="Platform"
        name={path("platform")}
        rules={[{ required: true, message: "Platform is required" }]}
        tooltip={
          platformLocked
            ? "Platform identifies the record and cannot be changed. Delete this tab and add a new one to switch."
            : undefined
        }
      >
        <Select placeholder="Please select Platform" options={platformOptions} disabled={platformLocked} />
      </Form.Item>

      <Form.Item
        label="Platform ID"
        name={path("platformId")}
        rules={[{ required: true, message: "Platform ID is required" }]}
      >
        <Input placeholder="e.g. com.example.game" />
      </Form.Item>

      <Form.Item
        label="Icon URL"
        name={path("iconUrl")}
        rules={[
          { required: true, message: "Icon URL is required" },
          { type: "url", message: "Please enter a valid URL" },
        ]}
      >
        <Input placeholder="Please enter Icon URL" />
      </Form.Item>

      <Form.Item
        label="Rating"
        name={path("rating")}
        rules={[{ required: true, message: "Rating is required" }]}
      >
        <Input type="number" placeholder="Please enter Rating" min={1} max={5} step={0.1} />
      </Form.Item>

      <Form.Item
        label="Install Revenue"
        name={path("installRevenue")}
        rules={[{ required: true, message: "Install Revenue is required" }]}
      >
        <Input type="number" placeholder="Please enter Install Revenue" min={0} step={0.01} />
      </Form.Item>

      <Form.Item
        label="AppsFlyer Install Revenue"
        name={path("appsflyerRevenue")}
        tooltip="When set, sent to AppsFlyer on game install instead of Install Revenue. Other analytics are unaffected."
      >
        <Input type="number" placeholder="Leave empty to use Install Revenue" min={0} step={0.01} />
      </Form.Item>

      <Form.Item label="Adjust Link Token" name={path("adjustLinkToken")}>
        <Input placeholder="Please enter Adjust Link Token" />
      </Form.Item>

      <Form.Item
        label="CTA Link"
        name={path("ctaLink")}
        rules={[{ required: true, message: "CTA link is required" }, urlValidator()]}
      >
        <Input placeholder="Please enter CTA Link" />
      </Form.Item>

      <Form.Item
        label="CTA Campaign"
        name={path("ctaCampaign")}
        rules={[{ required: true, message: "CTA campaign is required" }]}
      >
        <Input placeholder="Please enter CTA Campaign" />
      </Form.Item>

      <Form.Item
        label="Attribution Platform"
        name={path("attributionPlatform")}
        rules={[{ required: true, message: "Attribution platform is required" }]}
      >
        <Select placeholder="Please select attribution platform" options={attributionPlatformOptions} />
      </Form.Item>

      <Form.Item
        label="Game Publisher"
        name={path("gamePublisherId")}
        rules={[{ required: true, message: "Game publisher is required" }]}
      >
        <Select
          placeholder="Please select Game Publisher"
          options={publishers.map(p => ({ label: p.name, value: p.id }))}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>

      <Form.Item
        label="Attribution Window (days)"
        name={path("attributionWindowDays")}
        rules={[
          {
            validator: (_, value) => {
              if (value === undefined || value === null || value === "") return Promise.resolve()
              const num = Number(value)
              if (isNaN(num) || num < 1) return Promise.reject(new Error("Minimum value is 1"))

              return Promise.resolve()
            },
          },
        ]}
      >
        <Input type="number" placeholder="Empty = unlimited" min={1} />
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: 14 }}>
        Per-platform budget
      </Divider>

      <Form.Item label="Is Installable" name={path("isInstallable")} valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item label="Is Budget Active" name={path("isBudgetActive")} valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) => prev.platforms?.[index]?.isBudgetActive !== cur.platforms?.[index]?.isBudgetActive}
      >
        {({ getFieldValue }) => {
          const active = getFieldValue(["platforms", index, "isBudgetActive"])

          return (
            <>
              <Form.Item
                label="Daily Budget"
                name={path("dailyBudget")}
                rules={[{ required: active, message: "Daily Budget is required when active" }]}
              >
                <Input type="number" placeholder="USD" min={0} step={0.01} disabled={!active} />
              </Form.Item>
              <Form.Item
                label="Monthly Budget"
                name={path("monthlyBudget")}
                rules={[{ required: active, message: "Monthly Budget is required when active" }]}
              >
                <Input type="number" placeholder="USD" min={0} step={0.01} disabled={!active} />
              </Form.Item>
            </>
          )
        }}
      </Form.Item>
    </>
  )
}
